import { useImagingOrder, usePatient } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { imagingOrderFormSchema, type ImagingOrderFormValues } from '@schema/imagingOrder.schema'
import { ImagingOrderCreatePayload } from '@type/imaging_order.type'
import { FormButton, Modal, SelectInputField, TextAreaField } from '@ui'
import { Select } from '@ui/SearchInput/Select'
import { getError, showToast } from '@util'
import { buildImagingOrderPayload, newFreeTextOption, searchTests, type TestLineOption } from '@util/testCatalogue'
import { debounce } from 'lodash'
import { useCallback, useEffect, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useParams, useSearchParams } from 'react-router-dom'
import type { GroupBase } from 'react-select'
import { OptionTag, TestLineRows } from './TestLineRows'

type Props = {
  onClose: () => void
  patientId?: string
  encounterId?: string
}

type ExtendedImagingOrderFormValues = ImagingOrderFormValues & { patient_id?: string }

const AddImagingOrder = ({ onClose, patientId: patientIdProp, encounterId: encounterIdProp }: Props) => {
  const { patientId: patientIdParam, encounterId: encounterIdParam } = useParams<{
    patientId: string
    encounterId: string
  }>()
  const resolvedPatientId = patientIdProp ?? patientIdParam
  const resolvedEncounterId = encounterIdProp ?? encounterIdParam
  const [, setSearchParams] = useSearchParams()
  const methods = useForm<ExtendedImagingOrderFormValues>({
    defaultValues: { imaging_studies: [], note: '' },
    resolver: yupResolver(imagingOrderFormSchema)
  })
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting }
  } = methods

  // Mutations only — do not mount an unfiltered list query that races the page list.
  const { createImagingOrder } = useImagingOrder()

  const { singlePatient } = usePatient({ id: resolvedPatientId })

  const [patientSearch, setPatientSearch] = useState('')
  const debouncedSetPatientSearch = useCallback(
    debounce((q: string) => setPatientSearch(q), 400),
    []
  )
  const handlePatientSearch = (q: string) => debouncedSetPatientSearch(q)

  const { patients } = usePatient({ query: patientSearch, page: 1, limit: 50 })

  // If the modal was opened with an existing patient, populate the default value.
  useEffect(() => {
    if (singlePatient) {
      setValue('patient_id', String(singlePatient.id))
    } else if (resolvedPatientId) {
      setValue('patient_id', String(resolvedPatientId))
    }
  }, [singlePatient, resolvedPatientId, setValue])

  // Facility list + shared catalogue; free text still allowed.
  const loadImagingOptions = useCallback((inputValue: string) => searchTests('imaging', inputValue), [])

  const onSubmit = async (form: ExtendedImagingOrderFormValues) => {
    const finalPatientId = (watch('patient_id') as string | undefined) ?? resolvedPatientId
    if (!finalPatientId) {
      showToast('Missing patient')
      return
    }

    const payload = buildImagingOrderPayload(
      finalPatientId,
      form.imaging_studies,
      form.note ?? '',
      resolvedEncounterId
    ) as ImagingOrderCreatePayload

    try {
      await createImagingOrder(payload)
      // Radiology list is newest-first on page 1; clear filters that would hide the new row.
      if (!resolvedEncounterId) {
        setSearchParams(prev => {
          prev.delete('query')
          prev.delete('captured_date')
          prev.set('status', 'all')
          prev.set('page', '1')
          return prev
        })
      }
      showToast('Imaging order placed successfully')
      onClose()
    } catch (error) {
      getError(error)
    }
  }

  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Order imaging">
        <form className="flex min-w-[530px] flex-col gap-4 overflow-auto" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-4 overflow-auto p-6">
            {!resolvedPatientId && (
              <SelectInputField
                dropProps={{ align: { left: 'left', right: 'right' }, height: '200px' }}
                label="Search patient"
                labelKey={({ attributes }) => `${attributes.first_name} ${attributes.last_name}`}
                name="patient_id"
                onSearch={handlePatientSearch}
                options={patients || []}
                placeholder="Search Patient"
                searchPlaceholder="eg John doe"
                valueKey={{ key: 'id', reduce: true }}
              />
            )}

            <Controller
              control={control}
              name="imaging_studies"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <>
                  <Select<TestLineOption, true, GroupBase<TestLineOption>>
                    borderRadius={'15px'}
                    defaultOptions
                    error={error?.message}
                    formatOptionLabel={(option, { context }) => (
                      <span>
                        {option.label}
                        {context === 'menu' ? <OptionTag option={option} /> : null}
                      </span>
                    )}
                    getNewOptionData={(inputValue, optionLabel) => newFreeTextOption(inputValue, optionLabel)}
                    getOptionLabel={o => o.label}
                    getOptionValue={o => String(o.value)}
                    isAsync
                    isCreatable
                    isMulti
                    label="Study"
                    loadOptions={loadImagingOptions}
                    onChange={opts => onChange((opts as TestLineOption[] | null) ?? [])}
                    placeholder="Search studies (e.g. chest X-ray) or type a name"
                    value={value as TestLineOption[]}
                  />
                  <TestLineRows kind="imaging" lines={(value as TestLineOption[]) ?? []} onChange={onChange} />
                </>
              )}
            />

            <TextAreaField label="Note to radiology" name="note" placeholder="Enter Note" />
          </div>
          <FormButton isLoading={isSubmitting} onClose={onClose} submit="Order" />
        </form>
      </Modal>
    </FormProvider>
  )
}

export default AddImagingOrder
